import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { Document } from '../../types'

interface CaseDocumentPreviewModalProps {
  document: Document | null
  isOpen: boolean
  onClose: () => void
}

export default function CaseDocumentPreviewModal({ document, isOpen, onClose }: CaseDocumentPreviewModalProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && document) {
      loadPreview()
    } else {
      setPreviewUrl(null)
      setError(null)
    }
  }, [isOpen, document])

  const loadPreview = async () => {
    if (!document) return

    setLoading(true)
    setError(null)

    try {
      // Use the server API for both PDF and image files to avoid CORS issues
      const response = await fetch(`/api/documents/${document.id}/download`)

      if (!response.ok) {
        throw new Error('Failed to load document preview')
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      setPreviewUrl(url)

      // Clean up blob URL when component unmounts
      return () => {
        URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error('Error loading document preview:', err)
      setError('Failed to load document preview')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (document) {
      try {
        const response = await fetch(`/api/documents/${document.id}/download`)
        if (!response.ok) {
          throw new Error('Failed to download document')
        }
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = window.document.createElement('a')
        a.href = url
        a.download = document.originalName || document.fileName
        window.document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        window.document.body.removeChild(a)
      } catch (error) {
        console.error('Failed to download document:', error)
      }
    }
  }

  if (!isOpen || !document) return null

  const isPdf = document.mimeType === 'application/pdf'
  const isImage = document.mimeType.startsWith('image/')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="h-6 w-6 text-gray-400" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">{document.originalName}</h3>
              <p className="text-sm text-gray-500">
                {document.type.replace(/_/g, ' ')} • {(document.fileSize / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Download document"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-6">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading preview...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={handleDownload}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 mx-auto"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Download Instead</span>
                </button>
              </div>
            </div>
          )}

          {previewUrl && !loading && !error && (
            <div className="w-full h-full flex items-center justify-center">
              {isPdf && (
                <iframe
                  src={previewUrl}
                  className="w-full h-full border border-gray-200 rounded"
                  title={`PDF preview: ${document.originalName}`}
                />
              )}

              {isImage && (
                <div className="max-w-full max-h-full flex items-center justify-center">
                  <img
                    src={previewUrl}
                    alt={document.originalName}
                    className="max-w-full max-h-full object-contain rounded shadow-lg"
                  />
                </div>
              )}
            </div>
          )}

          {!previewUrl && !loading && !error && !isPdf && !isImage && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                <button
                  onClick={handleDownload}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 mx-auto"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Download Document</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>
              {document.case ? (
                <>
                  Case: {document.case.caseNumber} • {document.case.family.firstName} {document.case.family.lastName}
                </>
              ) : (
                'Unknown case'
              )}
              {' • '}
              Uploaded: {new Date(document.uploadedAt).toLocaleDateString()} • {new Date(document.uploadedAt).toLocaleTimeString()}
            </div>
            <div className="flex items-center space-x-4">
              {document.verifiedAt && (
                <span>Verified: {new Date(document.verifiedAt).toLocaleDateString()}</span>
              )}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                document.status === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                document.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                document.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {document.status.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}